import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Container, Text, Sprite } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  selectFreeRoundsCount,
  selectFreeRoundsPerformed,
} from "../../../../State/selectors";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";

// Constants
const COUNTER_Y_OFFSET_MULTIPLIER = 2.05; // Position below symbol center (same as HoldAndWinCounter)

// Free rounds counter badge configuration - device multipliers only
const FREE_ROUNDS_COUNTER_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      deviceMultiplier: 1.2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 1.2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      deviceMultiplier: 1.1,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 1.1,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    deviceMultiplier: 1.0,
  },
};

/**
 * FreeRoundsCounter - Displays casino-granted free rounds progress
 *
 * Shows free rounds progress:
 * - When performed=0: "Free Rounds: 10" (just total count)
 * - When performed>0: "Free Rounds: 3/10" (performed/total format)
 * - Updates immediately when spin starts (optimistic update)
 * - Hidden during bonus game (Hold and Win)
 * - Hidden only when completion popup shows (stays visible during animations)
 * - Yellow badge background with black text
 * - Positioned at bottom center of reels (similar to HoldAndWinCounter)
 *
 * NOTE: Bet switching to free rounds locked bet happens in ErrorRecoveryOverlay
 * during the black screen transition (invisible to user)
 */
const FreeRoundsCounter = () => {
  const { t } = useTranslation();
  const { device, orientation, symbolScale, symbolHeight } = useDimensions();

  // Redux state
  const freeRoundsCount = useSelector(selectFreeRoundsCount);
  const freeRoundsPerformed = useSelector(selectFreeRoundsPerformed);

  // Split label and numbers for different styling
  const labelText = `${t("free_rounds")}: `;
  const numbersText =
    freeRoundsPerformed === 0
      ? `${freeRoundsCount}`
      : `${freeRoundsPerformed}/${freeRoundsCount}`;
  const fullText = labelText + numbersText;

  // Get texture for background badge
  const texture = PIXI.Assets.get(ASSET_NAME.free_rounds_background);

  // Get device config
  const getDeviceConfig = () => {
    return (
      FREE_ROUNDS_COUNTER_CONFIG[device]?.[orientation] ||
      FREE_ROUNDS_COUNTER_CONFIG[device] || { deviceMultiplier: 1.0 }
    );
  };

  const deviceConfig = getDeviceConfig();

  // Calculate scaled dimensions using native texture size and maintaining aspect ratio
  const getScaledDimensions = useMemo(() => {
    if (!texture) return { scaledWidth: 0, scaledHeight: 0 };
    const nativeWidth = texture.width;
    const nativeHeight = texture.height;
    const scaledWidth =
      nativeWidth * deviceConfig.deviceMultiplier * symbolScale;
    const scaledHeight =
      nativeHeight * deviceConfig.deviceMultiplier * symbolScale;
    return { scaledWidth, scaledHeight };
  }, [texture, deviceConfig.deviceMultiplier, symbolScale]);

  const { scaledWidth, scaledHeight } = getScaledDimensions;

  // Calculate ideal font size based on badge height
  const idealFontSize = useMemo(() => {
    return Math.round(scaledHeight * 0.5); // 50% of badge height
  }, [scaledHeight]);

  // Calculate max width for text (with padding)
  const maxTextWidth = useMemo(() => {
    return scaledWidth * 0.85; // 85% of badge width for padding
  }, [scaledWidth]);

  // Auto-scaling font size calculation
  const finalFontSize = useMemo(() => {
    // Create temp style to measure text (using bold for measurement since numbers are bold)
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
      align: "center",
    });

    // Measure actual text dimensions using full text
    const metrics = TextMetrics.measureText(fullText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits
    return idealFontSize;
  }, [fullText, idealFontSize, maxTextWidth]);

  // Text style for label (normal weight)
  const labelStyle = useMemo(() => {
    return new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalFontSize,
      fontWeight: "500", // Normal weight
      fill: 0x000000, // Black color
      align: "left",
    });
  }, [finalFontSize]);

  // Text style for numbers (bold)
  const numbersStyle = useMemo(() => {
    return new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalFontSize,
      fontWeight: "700", // Bold
      fill: 0x000000, // Black color
      align: "left",
    });
  }, [finalFontSize]);

  // Calculate text positions for proper alignment
  const textPositions = useMemo(() => {
    const labelMetrics = TextMetrics.measureText(labelText, labelStyle);
    const numbersMetrics = TextMetrics.measureText(numbersText, numbersStyle);
    const totalWidth = labelMetrics.width + numbersMetrics.width;

    // Center the combined text
    const startX = -totalWidth / 2;
    const labelX = startX;
    const numbersX = startX + labelMetrics.width;

    return { labelX, numbersX };
  }, [labelText, numbersText, labelStyle, numbersStyle]);

  if (!texture) {
    return null;
  }

  return (
    <Container
      name="free_rounds_counter"
      x={0}
      y={symbolHeight * COUNTER_Y_OFFSET_MULTIPLIER}
      zIndex={10}
    >
      {/* Yellow background badge */}
      <Sprite
        texture={texture}
        name="free_rounds_background"
        anchor={0.5}
        width={scaledWidth}
        height={scaledHeight}
        x={0}
        y={0}
      />
      {/* Counter text - label (normal weight) */}
      <Text
        text={labelText}
        style={labelStyle}
        anchor={{ x: 0, y: 0.5 }}
        x={textPositions.labelX}
        y={0}
        resolution={2}
      />
      {/* Counter text - numbers (bold) */}
      <Text
        text={numbersText}
        style={numbersStyle}
        anchor={{ x: 0, y: 0.5 }}
        x={textPositions.numbersX}
        y={0}
        resolution={2}
      />
    </Container>
  );
};

export default FreeRoundsCounter;
